import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  Space,
  Tooltip,
  DatePicker,
  Select,
  Divider,
  Alert,
  Radio,
  Switch,
  Tabs,
  Button,
  Upload,
  message,
  Modal,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import {
  countryOptions,
  focusCommodityOptions,
  commodityOptions,
  yesNoOptions,
  currencyOptions,
} from "../../../store/static";
import { selectProps, getFieldDisableStatusForCommodity } from "../../../lib";
import { AreaUnitFields, SegmentForm, SegmentConfigurationForm } from ".";
import { UIState } from "../../../store";
import dayjs from "dayjs";
import { CaseUIState, CurrentCaseState } from "../store";
import { MAX_SEGMENT } from "../constants";
import { uniqBy } from "lodash";
import { QuestionCircleOutline } from "../../../lib/icon";
import {
  FileExcelOutlined,
  DeleteOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { api } from "../../../lib";

const responsiveCol = {
  xs: { span: 24 },
  sm: { span: 24 },
  md: { span: 24 },
  lg: { span: 12 },
  xl: { span: 12 },
};

const livestockPrompt = (
  <>
    In the case of multiple by-products from livestock, please insert
    information for each by-product separately. For example, in the case of
    cows, consider meat as the secondary commodity income source and milk as the
    tertiary income source. Only enter information for these by-products in
    detail if there is a commercial production system. If you do not have
    detailed information on the production system, we recommend inserting values
    on the next page under &apos;diversified income&apos; -&gt; &apos;income
    from livestock&apos;.
  </>
);

const { Dragger } = Upload;

const SecondaryForm = ({
  index,
  indexLabel,
  disabled,
  disableAreaSizeUnitField,
  disableLandUnitField,
  disableDataOnIncomeDriverField,
}) => {
  const form = Form.useFormInstance();
  const caseUI = CaseUIState.useState((s) => s);

  const updateCaseUI = (key, value) => {
    CaseUIState.update((s) => ({
      ...s,
      [key]: value,
    }));
  };

  const handleOnChangeCommodity = (value) => {
    const { disableLandUnitField, disableDataOnIncomeDriverField } =
      getFieldDisableStatusForCommodity(value);
    const updatedValue = {
      ...caseUI[index],
      disableLandUnitField,
      disableDataOnIncomeDriverField,
    };
    // reset breakdown value land unit on disableLandUnitField
    if (disableLandUnitField) {
      form.setFieldValue(`${index}-area_size_unit`, null);
    }
    // reset breakdown value when disableDataOnIncomeDriverField
    if (disableDataOnIncomeDriverField) {
      form.setFieldValue(`${index}-breakdown`, null);
    }
    updateCaseUI(index, updatedValue);
  };

  return (
    <>
      <Col span={24}>
        <Form.Item
          name={`${index}-breakdown`}
          label={`Data on income drivers available?`}
          rules={[
            {
              required: !disabled && !disableDataOnIncomeDriverField,
              message: "Please select yes or no",
            },
          ]}
        >
          <Radio.Group
            disabled={disabled || disableDataOnIncomeDriverField}
            options={yesNoOptions}
            onChange={(e) =>
              updateCaseUI(index, {
                ...caseUI[index],
                disableAreaSizeField: e.target.value ? false : true,
              })
            }
          />
        </Form.Item>
      </Col>
      <Col {...responsiveCol}>
        <Form.Item
          name={`${index}-commodity`}
          label={`${indexLabel} Commodity`}
          rules={[
            {
              required: !disabled,
              message: "Commodity is required",
            },
          ]}
        >
          <Select
            placeholder={`Add your ${indexLabel} Commodity`}
            disabled={disabled}
            options={commodityOptions}
            onChange={handleOnChangeCommodity}
            {...selectProps}
          />
        </Form.Item>
      </Col>
      <Col {...responsiveCol}>
        <AreaUnitFields
          disabled={disabled || disableAreaSizeUnitField}
          index={index}
          disableLandUnitField={disableLandUnitField}
        />
      </Col>
      <Col span={24}>
        {/* PROMPT */}
        {disableLandUnitField ? (
          <Alert
            style={{
              marginTop: "-10px",
              marginBottom: "24px",
              fontSize: "13px",
            }}
            message={livestockPrompt}
            type="info"
          />
        ) : (
          ""
        )}
      </Col>
    </>
  );
};

const SegmentOverflowAlert = ({ segments = [] }) => {
  const segmentCount = segments?.length || 0;
  if (segmentCount <= MAX_SEGMENT) {
    return null;
  }
  return (
    <Alert
      message={`You have defined ${segmentCount} segments. The maximum allowed is ${MAX_SEGMENT}. Please remove ${
        segmentCount - MAX_SEGMENT
      } segment(s) to save your changes.`}
      type="error"
      style={{
        marginBottom: "16px",
        display: "flex",
        alignItems: "flex-start",
      }}
    />
  );
};

const CaseForm = ({
  deletedSegmentIds = [],
  updateCurrentCase = () => {},
  setDeletedSegmentIds = () => {},
  dataUploadFieldPreffix = "",
  onTabChange = () => {},
  activeTab = "manual",
}) => {
  const form = Form.useFormInstance();
  const tagOptions = UIState.useState((s) => s.tagOptions);
  const companyOptions = UIState.useState((s) => s.companyOptions);
  const currentCase = CurrentCaseState.useState((s) => s);
  const { secondary, tertiary, general } = CaseUIState.useState((s) => s);
  const { enableEditCase, enableDataUpload } = general;

  const [messageApi, contextHolder] = message.useMessage();
  const [uploadResult, setUploadResult] = useState(null);
  const isUpdateMode = !!currentCase?.id;

  const segments = Form.useWatch("segments", form);
  const importId = Form.useWatch("import_id", form);

  const isAtMaxSegments = (segments?.length || 0) >= MAX_SEGMENT;

  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadErrorText, setUploadErrorText] = useState("");
  const [downloading, setDownloading] = useState(false);

  const resetDataUploadForm = () => {
    setUploadResult(null);
    setFileList([]);

    // In update mode, preserve segments with IDs
    const existingSegments = isUpdateMode
      ? segments?.filter((s) => s?.id) || []
      : [];

    form.setFieldsValue({
      import_id: null,
      // reset variable type
      [`${dataUploadFieldPreffix}variable_type`]: null,
      // reset segmentation variable when variable type changes
      [`${dataUploadFieldPreffix}segmentation_variable`]: null,
      // reset segments
      segments:
        existingSegments.length > 0 ? existingSegments : [{ name: null }],
      // reset number of segments
      [`${dataUploadFieldPreffix}number_of_segments`]: null,
    });
  };

  const handleTabChange = (key) => {
    // Only guard if NOT in update mode and there is existing data
    const hasData = (segments?.length > 0 && segments[0]?.name) || !!importId;

    if (!isUpdateMode && hasData) {
      Modal.confirm({
        title: "Switch segmentation method?",
        icon: <ExclamationCircleOutlined />,
        content:
          "Switching tabs will clear your current segmentation progress. Do you want to continue?",
        okText: "Yes, switch and clear",
        cancelText: "Cancel",
        okButtonProps: {
          style: { backgroundColor: "#01625F", borderColor: "#01625F" },
        },
        onOk: () => {
          resetDataUploadForm();
          onTabChange(key);
        },
      });
    } else {
      onTabChange(key);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    accept: ".xlsm,application/vnd.ms-excel.sheet.macroEnabled.12",
    disabled: uploading,
    fileList: fileList, // Controlled file list
    customRequest: async ({ file, onSuccess, onError, onProgress }) => {
      setUploading(true);
      try {
        const response = await api.upload("/case-import", file, {
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress({ percent });
          },
        });
        const result = response.data;
        setUploadResult(result);
        if (result.import_id) {
          form.setFieldValue("import_id", result.import_id);
        }
        onSuccess(result, file);
        messageApi.success(`${file.name} uploaded successfully`);
      } catch (error) {
        console.error("Upload error:", error);
        onError(error);
        const { status, data } = error.response;
        let errorText = `${file.name} upload failed.`;
        if (status === 400 && data.detail) {
          errorText = `${errorText} ${data.detail}`;
          setUploadErrorText(data.detail);
        }
        messageApi.error(errorText);
      } finally {
        setUploading(false);
      }
    },
    onChange(info) {
      const { status, fileList: newFileList } = info;
      setFileList(newFileList); // Update file list state

      if (status === "uploading") {
        console.info("Progress:", info.file.percent + "%");
      }
      if (status === "done") {
        const result = info.file.response;
        console.info("Upload completed with result:", result);
      } else if (status === "error") {
        console.error("Upload failed");
      }
    },
    onRemove: () => {
      // Clear upload result and form when file is removed
      setUploadResult(null);
      setFileList([]);
      resetDataUploadForm();
      return true;
    },
    showUploadList: {
      showRemoveIcon: true,
      removeIcon: <DeleteOutlined />,
    },
    itemRender: (originNode, file) => {
      const isError = file.status === "error";
      const isSuccess = file.status === "done";

      return (
        <div
          className="case-form-dragger-item-container"
          style={{
            border: `1px solid ${
              isError ? "#ff4d4f" : isSuccess ? "#52c41a" : "#d9d9d9"
            }`,
            backgroundColor: isError
              ? "#fff2f0"
              : isSuccess
              ? "#f6ffed"
              : "#fafafa",
          }}
        >
          <div className="dragger-item-list-wrapper">
            <FileExcelOutlined
              style={{
                color: isError ? "#ff4d4f" : isSuccess ? "#01625f" : "#1890ff",
                fontSize: "36px",
                marginRight: "12px",
              }}
            />
            <div style={{ flex: 1 }}>
              <h4
                style={{
                  color: isError
                    ? "#ff4d4f"
                    : isSuccess
                    ? "#01625f"
                    : "#262626",
                }}
              >
                {file.name}
              </h4>
              {file.status === "uploading" && (
                <p
                  style={{
                    color: "#8c8c8c",
                  }}
                >
                  Uploading: {file.percent}%
                </p>
              )}
              {isError && (
                <div
                  style={{
                    color: "#ff4d4f",
                  }}
                >
                  Upload failed. {uploadErrorText}
                </div>
              )}
              {isSuccess && (
                <p
                  style={{
                    color: "#01625f",
                  }}
                >
                  Uploaded successfully
                </p>
              )}
            </div>
          </div>
          {(isError || isSuccess) && (
            <Button
              type="text"
              danger={isError}
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                // Trigger the onRemove handler
                setFileList([]);
                setUploadResult(null);
                resetDataUploadForm();
              }}
            >
              Remove
            </Button>
          )}
        </div>
      );
    },
    maxCount: 1,
  };

  const updateCaseUI = (key, value) => {
    CaseUIState.update((s) => ({
      ...s,
      [key]: value,
    }));
  };

  const filteredCurrencyOptions = useMemo(() => {
    if (!currentCase.country) {
      return uniqBy(currencyOptions, "value");
    }
    const countryCurrency = currencyOptions.find(
      (co) => co.country === currentCase.country
    );
    // set default currency value
    if (!currentCase?.id) {
      updateCurrentCase("currency", countryCurrency?.value);
    }
    let additonalCurrencies = currencyOptions.filter((co) =>
      ["eur", "usd"].includes(co.value.toLowerCase())
    );
    additonalCurrencies = uniqBy(additonalCurrencies, "value");
    return uniqBy(
      [countryCurrency, ...additonalCurrencies].filter(Boolean),
      "value"
    );
  }, [currentCase, updateCurrentCase]);

  useEffect(() => {
    // set default currency value
    if (!currentCase?.id && currentCase?.currency) {
      form.setFieldValue("currency", currentCase.currency);
    }
  }, [form, currentCase]);

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    api
      .download("/case-import/download-template", {
        filename: "data_upload_template.zip",
      })
      .catch((error) => {
        console.error("Download template error:", error);
        messageApi.error("Failed to download template.");
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  return (
    <Row gutter={[16, 16]} className="case-form-body-wrapper">
      {/* GENERAL INFORMATION */}
      <Col span={24}>
        <Card
          title="General Information"
          className="case-setting-child-card-wrapper"
          size="small"
          extra={
            <Form.Item style={{ marginBottom: 0 }}>
              <Switch
                size="small"
                checked={currentCase.private}
                onChange={(value) => updateCurrentCase("private", value)}
                disabled={!enableEditCase}
              />{" "}
              Private case{" "}
              <Tooltip title="The details of a private case are visible only to you and any users you choose to add to the case.">
                <span>
                  <QuestionCircleOutline size={14} />
                </span>
              </Tooltip>
            </Form.Item>
          }
        >
          <Row gutter={[12, 12]}>
            <Col span={12}>
              <Form.Item
                label="Name of case"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Name of case is required",
                  },
                ]}
              >
                <Input disabled={!enableEditCase} />
              </Form.Item>
              <Form.Item
                label="Case description"
                name="description"
                rules={[
                  {
                    required: true,
                    message: "Case description is required",
                  },
                ]}
              >
                <Input.TextArea rows={5} disabled={!enableEditCase} />
              </Form.Item>
              <Form.Item
                name="country"
                label="Country"
                rules={[
                  {
                    required: true,
                    message: "Country is required",
                  },
                ]}
              >
                <Select
                  placeholder="Select country"
                  options={countryOptions}
                  {...selectProps}
                  onChange={(value) => updateCurrentCase("country", value)}
                  disabled={!enableEditCase}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="company" label="Company">
                <Select
                  placeholder="Select company"
                  options={companyOptions}
                  {...selectProps}
                  disabled={!enableEditCase}
                />
              </Form.Item>
              <Form.Item
                name="year"
                label={
                  <Space align="center">
                    <div>Year of available data</div>
                    <Tooltip
                      title="Please enter the year for which you are providing data. This year will also be used to retrieve a living income benchmark, if it is available for the specified region. If a benchmark for this year is not available, we will use the most recent available benchmark value."
                      placement="topRight"
                    >
                      <span>
                        <QuestionCircleOutline size={14} />
                      </span>
                    </Tooltip>
                  </Space>
                }
                rules={[
                  {
                    required: true,
                    message: "Choose year",
                  },
                ]}
              >
                <DatePicker
                  picker="year"
                  disabledDate={(current) => {
                    const currentYear = dayjs().year();
                    const selectedYear = dayjs(current).year();
                    return selectedYear < 2020 || selectedYear > currentYear;
                  }}
                  disabled={!enableEditCase}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                label="Currency"
                name="currency"
                rules={[
                  {
                    required: true,
                    message: "Currency is required",
                  },
                ]}
              >
                <Select
                  placeholder="Select currency"
                  options={filteredCurrencyOptions}
                  {...selectProps}
                  disabled={!currentCase.country || !enableEditCase}
                />
              </Form.Item>
              <Form.Item
                name="tags"
                label="Tags"
                rules={[
                  {
                    required: true,
                    message: "Select at least one tag",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  placeholder="Add Tags"
                  options={tagOptions}
                  {...selectProps}
                  disabled={!enableEditCase}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Col>
      {/* COMMODITIES */}
      <Col span={24}>
        <Card
          title="Commodities"
          className="case-setting-child-card-wrapper"
          size="small"
        >
          {/* 1 */}
          <Row gutter={[12, 12]}>
            <Col span={24} className="section-title">
              Primary commodity
            </Col>
            <Col {...responsiveCol}>
              <Form.Item
                label="Commodity"
                name="focus_commodity"
                rules={[
                  {
                    required: true,
                    message: "Commodity is required",
                  },
                ]}
              >
                <Select
                  placeholder="Select primary commodity"
                  options={focusCommodityOptions}
                  {...selectProps}
                  disabled={!enableEditCase}
                />
              </Form.Item>
            </Col>
            <Col {...responsiveCol}>
              <AreaUnitFields form={form} disabled={!enableEditCase} />
            </Col>
          </Row>
          <Divider />
          {/* 2 */}
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <Space align="end">
                <div className="section-title">Secondary commodity</div>
                <Switch
                  size="small"
                  checked={secondary.enable}
                  onChange={(value) =>
                    updateCaseUI("secondary", { ...secondary, enable: value })
                  }
                  disabled={!enableEditCase}
                />
              </Space>
            </Col>
            <SecondaryForm
              index="secondary"
              indexLabel="Secondary"
              disabled={!secondary.enable || !enableEditCase}
              disableAreaSizeUnitField={secondary.disableAreaSizeField}
              disableLandUnitField={secondary.disableLandUnitField}
              disableDataOnIncomeDriverField={
                secondary.disableDataOnIncomeDriverField
              }
            />
          </Row>
          <Divider />
          {/* 3 */}
          <Row gutter={[12, 12]}>
            <Col span={24}>
              <Space align="end">
                <div className="section-title">Tertiary commodity</div>
                <Switch
                  size="small"
                  checked={tertiary.enable}
                  onChange={(value) =>
                    updateCaseUI("tertiary", { ...tertiary, enable: value })
                  }
                  disabled={!secondary.enable || !enableEditCase}
                />
              </Space>
            </Col>
            <SecondaryForm
              index="tertiary"
              indexLabel="Tertiary"
              disabled={!tertiary.enable || !enableEditCase}
              disableAreaSizeUnitField={tertiary.disableAreaSizeField}
              disableLandUnitField={tertiary.disableLandUnitField}
              disableDataOnIncomeDriverField={
                tertiary.disableDataOnIncomeDriverField
              }
            />
          </Row>
        </Card>
      </Col>
      {/* MANUAL / DATA UPLOAD */}
      <Col span={24}>
        {contextHolder}
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: "manual",
              label: "Manual data input",
              children: (
                <Col span={24}>
                  {/* SEGMENTATION */}
                  <Card
                    title="Create up to 5 segments"
                    className="case-setting-child-card-wrapper"
                    size="small"
                  >
                    <SegmentOverflowAlert segments={segments} />
                    <SegmentForm
                      deletedSegmentIds={deletedSegmentIds}
                      setDeletedSegmentIds={setDeletedSegmentIds}
                    />
                  </Card>
                </Col>
              ),
            },
            enableDataUpload && {
              key: "upload",
              label: "Data upload",
              children: (
                <Col span={24}>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <h3 style={{ marginBottom: "4px" }}>
                        Upload your completed data template
                      </h3>
                      <p
                        style={{
                          marginTop: 0,
                          marginBottom: "8px",
                          color: "rgba(0, 0, 0, 0.65)",
                        }}
                      >
                        Download the template, enter your data, run the
                        validation in Excel, and upload the validated file here.
                      </p>
                      {isUpdateMode && isAtMaxSegments && !uploadResult && (
                        <Alert
                          message={
                            <span>
                              You have reached the maximum of 5 segments. Please{" "}
                              <b>delete</b> existing segments from the{" "}
                              <b>&quot;Manual data input&quot;</b> tab before
                              uploading a new data template.
                            </span>
                          }
                          type="warning"
                          style={{
                            marginBottom: "16px",
                            display: "flex",
                            alignItems: "flex-start",
                          }}
                        />
                      )}
                      <Dragger
                        {...uploadProps}
                        style={{
                          marginBottom: "24px",
                          display:
                            (isUpdateMode && isAtMaxSegments) ||
                            (uploadProps.fileList &&
                              uploadProps.fileList.length > 0)
                              ? "none"
                              : "block",
                        }}
                      >
                        <p className="ant-upload-drag-icon">
                          <FileExcelOutlined />
                        </p>
                        <p className="ant-upload-text">
                          {uploading
                            ? "Uploading..."
                            : "Select a .xlsm data template to upload"}
                        </p>
                        <p className="ant-upload-hint">
                          {uploading
                            ? "Please wait while the file is being uploaded"
                            : "Drag and drop your data file here or click to upload"}
                        </p>
                        <Button
                          className="button-browse-file"
                          disabled={uploading}
                        >
                          {uploading ? "Uploading..." : "Browse files"}
                        </Button>
                      </Dragger>
                      <Form.Item name="import_id" hidden>
                        <input type="hidden" />
                      </Form.Item>
                    </Col>
                    {uploadResult && (
                      <Col span={24}>
                        <SegmentOverflowAlert segments={segments} />
                        <SegmentConfigurationForm
                          dataUploadFieldPreffix={dataUploadFieldPreffix}
                          uploadResult={uploadResult}
                          deletedSegmentIds={deletedSegmentIds}
                          setDeletedSegmentIds={setDeletedSegmentIds}
                          currentCase={currentCase}
                        />
                      </Col>
                    )}
                  </Row>
                </Col>
              ),
            },
          ].filter(Boolean)}
          tabBarExtraContent={
            enableDataUpload ? (
              <Button
                className="button-ghost"
                onClick={handleDownloadTemplate}
                disabled={downloading}
                loading={downloading}
              >
                <DownloadOutlined /> Download required data template (ZIP)
              </Button>
            ) : null
          }
        />
      </Col>
    </Row>
  );
};

export default CaseForm;
